import * as React from "react";
import { FloatingList } from "@floating-ui/react";
import clsx from "clsx";
import { isSameDay } from "date-fns";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
import { ListboxProvider } from "../listbox/listbox-context.mjs";
import { useListbox } from "../listbox/use-listbox.mjs";
import { Notice } from "../../notice/notice.mjs";
import { ScrollFade } from "../../scroll-fade/scroll-fade.mjs";
import { Spinner } from "../../spinner/spinner.mjs";
import { isDateRange, isEqualRange } from "./datepicker-helpers.mjs";
import { TimeOption } from "./time-option.mjs";
class TimeColumn extends React.PureComponent {
  constructor() {
    super(...arguments);
    this.hasExtraContent = () => {
      const { options } = this.props;
      if (!options) {
        return false;
      }
      for (const element of options) {
        if (Object.getOwnPropertyDescriptor(element, "extraContent")) {
          return true;
        }
      }
      return false;
    };
    this.hasToValue = () => {
      const { options } = this.props;
      if (!options) {
        return false;
      }
      for (const element of options) {
        if (Object.getOwnPropertyDescriptor(element, "to")) {
          return true;
        }
      }
      return false;
    };
    this.renderTime = (timeRange, isPlaceholder = false) => {
      const { formatFn, viewDate } = this.props;
      const { from, to, extraContent } = timeRange;
      if (!isPlaceholder && !isSameDay(from, viewDate)) {
        return null;
      }
      let timeString = formatFn(from, "HH:mm");
      if (to) {
        timeString += " - " + formatFn(to, "HH:mm");
      }
      const isLongPlaceholder = isPlaceholder ? !!to : this.hasToValue();
      return /* @__PURE__ */ React.createElement(
        TimeOption,
        {
          key: "".concat(from.valueOf(), "-").concat(to == null ? void 0 : to.valueOf()),
          label: timeString,
          value: timeRange,
          extraContent,
          placeholder: isLongPlaceholder ? "00:00 - 00:00" : "00:00"
        }
      );
    };
  }
  BEM() {
    return clsx("datepicker__column", "datepicker__column--time", {
      "datepicker__column--align-left": this.hasExtraContent()
    });
  }
  get value() {
    const { value } = this.props;
    return isDateRange(value) ? value : null;
  }
  render() {
    const { helper, options, placeholder, onSelect } = this.props;
    return /* @__PURE__ */ React.createElement("div", { className: this.BEM() }, helper ? /* @__PURE__ */ React.createElement(React.Fragment, null, placeholder && /* @__PURE__ */ React.createElement(TimeListbox, { disabled: true, "aria-hidden": "true" }, this.renderTime(placeholder, true)), helper.type === "loading" ? /* @__PURE__ */ React.createElement("div", { className: "datepicker__options-helper" }, /* @__PURE__ */ React.createElement(Spinner, { className: "datepicker__spinner", text: helper.content })) : /* @__PURE__ */ React.createElement("div", { className: "datepicker__options-helper" }, /* @__PURE__ */ React.createElement(Notice, { type: helper.type, direction: "vertical", size: "small", className: "datepicker__notice" }, helper.content))) : /* @__PURE__ */ React.createElement(TimeListbox, { value: this.value, onChange: (nextValue) => onSelect(nextValue, "minute") }, options == null ? void 0 : options.map((time) => this.renderTime(time))));
  }
}
const TimeListbox = ({ children, value, onChange, disabled, ...rest }) => {
  const labels = useLabels();
  const scrollRef = React.useRef(null);
  const context = useListbox({ value, onChange, valueCompareFn: isEqualRange, disabled });
  const { getFloatingProps, refs, elementsRef, labelsRef } = context;
  React.useEffect(() => {
    window.queueMicrotask(() => {
      if (scrollRef.current) {
        const optionToCenter = scrollRef.current.querySelector('[aria-selected="true"]');
        if (optionToCenter) {
          const { top, height } = scrollRef.current.getBoundingClientRect();
          scrollRef.current.scrollTop = optionToCenter.getBoundingClientRect().top - top - height / 2;
        }
      }
    });
  }, []);
  return /* @__PURE__ */ React.createElement(ListboxProvider, { value: context }, /* @__PURE__ */ React.createElement(ScrollFade, { elementRef: (el) => scrollRef.current = el }, /* @__PURE__ */ React.createElement("ul", { ...rest, className: "datepicker__options", ref: refs.setFloating, ...getFloatingProps(), "aria-label": labels.get("datepicker.timeLabel") }, /* @__PURE__ */ React.createElement(FloatingList, { elementsRef, labelsRef }, children))));
};
export {
  TimeColumn
};
